import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Table,
  Box,
  Button,
  Header,
  Popover,
  SpaceBetween,
  TextFilter,
  Select,
  Pagination,
  Link,
  CollectionPreferences,
} from '@cloudscape-design/components';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { useRules } from '../../hooks/useRules';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { useUrlSearchState } from '../../hooks/useUrlSearchState';
import { RuleStatusBadge } from '../../components/RuleStatusBadge';
import { ErrorState } from '../../components/ErrorState';
import { RESOURCE_TYPE_OPTIONS } from '../../constants/resourceTypes';
import type { RuleSummary } from '@amzn/helianthus-arbiter-client';
import { RuleStatus } from '@amzn/helianthus-arbiter-client';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatDateTime } from '../../utils/formatFieldName';

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: RuleStatus.ACTIVE },
  { label: 'Draft', value: RuleStatus.DRAFT },
  { label: 'Dry Run', value: RuleStatus.DRY_RUN },
  { label: 'Provisioning', value: RuleStatus.PROVISIONING },
  { label: 'Inactive', value: RuleStatus.INACTIVE },
  { label: 'Error', value: RuleStatus.ERROR },
  { label: 'Deprecated', value: RuleStatus.DEPRECATED },
];

export function RulesListPage() {
  useDocumentTitle('Rules');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Redirect: default status=active, merging into existing params (preserves ?language=llm etc.)
  useEffect(() => {
    if (!searchParams.has('status')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('status', RuleStatus.ACTIVE);
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const urlState = useUrlSearchState();

  // URL-backed filter state (replaces useState)
  // Use searchParams directly for status to avoid race condition on first load
  const resourceTypeFilter = urlState.get('resourceType');
  const statusFilter = searchParams.get('status') || RuleStatus.ACTIVE;
  const languageFilter = urlState.get('language');

  // Local text search state for responsive input (D-02)
  const [localSearchText, setLocalSearchText] = useState(urlState.get('q'));

  // Commit search text to URL on Enter or blur (D-02)
  const commitSearch = useCallback((text: string) => {
    urlState.set('q', text);
  }, [urlState]);

  // Read sort state from URL (D-09)
  const sortField = urlState.get('sort');
  const sortDir = urlState.get('dir');

  const { rules, loading, fetchingMore, error } = useRules({
    resourceType: resourceTypeFilter || undefined,
    ruleStatus: (statusFilter || undefined) as RuleStatus | undefined,
    limit: 50,
  });

  const languageOptions = useMemo(() => {
    const languages = [...new Set(
      rules.map((r) => r.expressionLanguage).filter(Boolean)
    )].sort();
    return [
      { label: 'All languages', value: '' },
      ...languages.map((lang) => ({ label: lang!, value: lang! })),
    ];
  }, [rules]);

  // Ignore stale URL values that don't match any current option so the dropdown display and
  // filtered results stay consistent (e.g., after status filter change loads a different rule set).
  const effectiveLanguageFilter = languageOptions.some((opt) => opt.value === languageFilter)
    ? languageFilter
    : '';

  useEffect(() => {
    if (
      languageFilter &&
      !loading &&
      rules.length > 0 &&
      !languageOptions.some((opt) => opt.value === languageFilter)
    ) {
      urlState.set('language', '');
    }
  }, [languageFilter, languageOptions, rules.length, loading, urlState]);

  const filteredRules = useMemo(() => {
    if (!effectiveLanguageFilter) return rules;
    return rules.filter((r) => r.expressionLanguage === effectiveLanguageFilter);
  }, [rules, effectiveLanguageFilter]);

  const {
    pageSize,
    columnDisplay,
    getColumnWidth,
    handleColumnWidthsChange,
    collectionPreferencesProps,
  } = useTablePreferences({
    storageKey: 'rules-table',
    defaults: {
      pageSize: 20,
      visibleContent: ['ruleName', 'status', 'severity', 'securityConcern', 'hasDependencies', 'appliesTo', 'lastModified'],
    },
    pageSizeOptions: [
      { value: 10, label: '10 rules' },
      { value: 20, label: '20 rules' },
      { value: 50, label: '50 rules' },
    ],
    visibleContentOptions: [
      {
        label: 'Columns',
        options: [
          { id: 'ruleName', label: 'Rule name', editable: false },
          { id: 'status', label: 'Status' },
          { id: 'severity', label: 'Severity' },
          { id: 'securityConcern', label: 'Security Concern' },
          { id: 'hasDependencies', label: 'Has Dependencies' },
          { id: 'appliesTo', label: 'Applies to' },
          { id: 'lastModified', label: 'Last modified' },
        ],
      },
    ],
  });

  // Column definitions extracted for sort column lookup (D-09, D-10, TABL-01)
  const columnDefinitions = useMemo(() => [
    {
      id: 'ruleName',
      header: 'Rule name',
      cell: (item: RuleSummary) => (
        <Link href={`/rules/${encodeURIComponent(item.ruleName!)}`} onFollow={(e) => { e.preventDefault(); navigate(`/rules/${encodeURIComponent(item.ruleName!)}`); }}>{item.ruleName}</Link>
      ),
      sortingField: 'ruleName',
      width: getColumnWidth('ruleName', 250),
      minWidth: 150,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: RuleSummary) => <RuleStatusBadge status={item.ruleStatus!} />,
      sortingField: 'ruleStatus',
      width: getColumnWidth('status', 120),
      minWidth: 100,
    },
    {
      id: 'severity',
      header: 'Severity',
      cell: (item: RuleSummary) => item.severity || '-',
      sortingField: 'severity',
      width: getColumnWidth('severity', 120),
      minWidth: 80,
    },
    {
      id: 'securityConcern',
      header: 'Security Concern',
      cell: (item: RuleSummary) =>
        item.securityConcern && item.securityConcern !== 'Unspecified' ? item.securityConcern : '-',
      sortingField: 'securityConcern',
      width: getColumnWidth('securityConcern', 200),
      minWidth: 120,
    },
    {
      id: 'hasDependencies',
      header: (
        <Popover
          header="Has Dependencies"
          content="Rules that this rule depends on for evaluation ordering. Dependent rules are evaluated first."
          dismissButton={false}
          position="top"
          size="medium"
          triggerType="text"
        >
          Has Dependencies
        </Popover>
      ),
      cell: (item: RuleSummary) => item.hasDependencies ? 'Yes' : 'No',
      sortingComparator: (a: RuleSummary, b: RuleSummary) => {
        const toNum = (v?: boolean): number => v ? 1 : 0;
        return toNum(a.hasDependencies) - toNum(b.hasDependencies);
      },
      width: getColumnWidth('hasDependencies', 140),
      minWidth: 100,
    },
    {
      id: 'appliesTo',
      header: 'Applies to',
      cell: (item: RuleSummary) => item.appliesTo?.join(', ') ?? '--',
      sortingComparator: (a: RuleSummary, b: RuleSummary) => {
        const aFirst = a.appliesTo?.[0] ?? '';
        const bFirst = b.appliesTo?.[0] ?? '';
        return aFirst.localeCompare(bFirst);
      },
      width: getColumnWidth('appliesTo', 200),
      minWidth: 120,
    },
    {
      id: 'lastModified',
      header: 'Last modified',
      cell: (item: RuleSummary) => formatDateTime(item.lastModifiedAt),
      sortingField: 'lastModifiedAt',
      width: getColumnWidth('lastModified', 200),
      minWidth: 150,
    },
  ], [navigate, getColumnWidth]);

  // Look up sorting column from URL param (D-09, Pitfall 5)
  const sortingColumn = useMemo(() => {
    if (!sortField) return undefined;
    return columnDefinitions.find(
      (col) => col.sortingField === sortField || col.id === sortField
    );
  }, [sortField, columnDefinitions]);

  // URL-committed search text for useCollection filtering (Pitfall 4)
  const committedSearchText = urlState.get('q');

  const { items, filteredItemsCount, actions, collectionProps, paginationProps } = useCollection(
    filteredRules,
    {
      filtering: {
        filteringFunction: (item, filteringText) => {
          const searchText = filteringText.toLowerCase();
          return (
            (item.ruleName?.toLowerCase().includes(searchText) ?? false) ||
            (item.appliesTo?.some((r) => r.toLowerCase().includes(searchText)) ?? false) ||
            (item.description?.toLowerCase().includes(searchText) ?? false) ||
            (item.securityConcern?.toLowerCase().includes(searchText) ?? false)
          );
        },
        defaultFilteringText: committedSearchText,
        empty: (
          <Box textAlign="center" color="inherit">
            <b>No rules</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No rules match the current filters.
            </Box>
            <Button onClick={() => navigate('/rules/create')}>Create rule</Button>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>No matches</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No rules match the search criteria.
            </Box>
          </Box>
        ),
      },
      pagination: { pageSize },
      sorting: {
        defaultState: sortingColumn
          ? { sortingColumn, isDescending: sortDir === 'desc' }
          : undefined,
      },
    }
  );

  // Sync back-navigation sort state (critical for SRCH-02)
  useEffect(() => {
    if (sortingColumn) {
      actions.setSorting({ sortingColumn, isDescending: sortDir === 'desc' });
    }
  }, [sortingColumn, sortDir, actions]);

  // Sync URL search text to useCollection filtering (back navigation)
  useEffect(() => {
    actions.setFiltering(committedSearchText);
  }, [committedSearchText, actions]);

  // Handle sort clicks -> URL (D-09)
  const handleSortingChange = useCallback(
    (event: { detail: { sortingColumn: { sortingField?: string; id?: string }; isDescending?: boolean } }) => {
      const field = event.detail.sortingColumn.sortingField ?? event.detail.sortingColumn.id ?? '';
      const dir = event.detail.isDescending ? 'desc' : 'asc';
      urlState.setMany({ sort: field, dir });
    },
    [urlState],
  );

  if (error) {
    return (
      <ErrorState
        variant="api-error"
        title="Error loading rules"
        message={error}
        onRetry={() => window.location.reload()}
        backLabel="Go to Dashboard"
        backHref="/"
      />
    );
  }

  return (
    <Table
      {...collectionProps}
      onSortingChange={handleSortingChange}
      loading={loading}
      loadingText="Loading rules..."
      resizableColumns
      columnDisplay={columnDisplay}
      onColumnWidthsChange={(event) =>
        handleColumnWidthsChange(
          columnDisplay.filter((c) => c.visible).map((c) => c.id),
          event,
        )
      }
      preferences={
        <CollectionPreferences
          {...collectionPreferencesProps}
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
        />
      }
      columnDefinitions={columnDefinitions}
      items={items}
      header={
        <Header
          variant="awsui-h1-sticky"
          actions={
            <Button variant="primary" onClick={() => navigate('/rules/create')}>
              Create rule
            </Button>
          }
          counter={loading ? undefined : `(${filteredItemsCount}${fetchingMore ? '+' : ''})`}
        >
          Rules
        </Header>
      }
      filter={
        <SpaceBetween size="m" direction="horizontal">
          <div
            onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(localSearchText); }}
            onBlur={() => commitSearch(localSearchText)}
          >
            <TextFilter
              filteringText={localSearchText}
              onChange={({ detail }) => setLocalSearchText(detail.filteringText)}
              filteringPlaceholder="Search rules"
              countText={`${filteredItemsCount} match${filteredItemsCount === 1 ? '' : 'es'}`}
            />
          </div>
          <Select
            selectedOption={RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === resourceTypeFilter) || RESOURCE_TYPE_OPTIONS[0]}
            onChange={({ detail }) => {
              urlState.set('resourceType', detail.selectedOption.value || '');
            }}
            options={RESOURCE_TYPE_OPTIONS}
            selectedAriaLabel="Selected"
          />
          <Select
            selectedOption={languageOptions.find((opt) => opt.value === languageFilter) || languageOptions[0]}
            onChange={({ detail }) => {
              urlState.set('language', detail.selectedOption.value || '');
            }}
            options={languageOptions}
            placeholder="All languages"
            selectedAriaLabel="Selected"
          />
          <Select
            selectedOption={STATUS_OPTIONS.find((opt) => opt.value === statusFilter) || STATUS_OPTIONS[0]}
            onChange={({ detail }) => {
              urlState.set('status', detail.selectedOption.value || '');
            }}
            options={STATUS_OPTIONS}
            selectedAriaLabel="Selected"
          />
        </SpaceBetween>
      }
      pagination={<Pagination {...paginationProps} />}
    />
  );
}
